import Phaser from 'phaser';
import FBHelper from '../utils/misc/FBHelper';
import TextHelpers from '../utils/misc/TextHelpers';
import {isIOS} from '../utils/PhaserUtils';
import BitmapTextInput from './BitmapTextInput';
import config from '../constants/Config';
import _ from 'underscore';

class GameOverScreen extends Phaser.Group {
    FADE_SPEED = 500;

    PAGE_CHANGE_SPEED = 500;

    PAGE_CHANGE_EASING = Phaser.Easing.Quartic;

    titleColor = 0xff2525;

    pageChangeIncomplete = false;

    defaultTextStyles = {
        font: 'skeefontBold',
        fill: 0xFFFFFF,
        fontSize: 52,
        align: 'center',
        lineHeight: 1.5
    }

    buttonStyles = {
        regular: {
            font: 'skeefontBold',
            fontSize: 40,
            padding: 20,
            borderRadius: 15,
            background: 0xFFFFFF,
            backgroundAlpha: 0.3
        },
        hover: {
            backgroundAlpha: 0.4
        }
    }

    content = {
        scoreBlock: [
            ['GAME OVER', {fill: this.titleColor, fontSize: 68}],
            ['TOTAL SCORE', {font: 'skeefont'}],
            ['<%-score%>', {fontSize: 62, lineHeight: 1.3}],
            ['CONGRATS!', {fontSize: 46}]
        ],
        madeTop: [
            ['YOU MADE THE TOP 10', {fontSize: 28, font: 'skeefont'}]
        ],

        namePrompt: [
            ['TYPE IN YOUR INITIALS', {fontSize: 20, font: 'skeefont'}]
        ],
        leaderTitle: [
            ['LEADERBOARD', {fill: this.titleColor, fontSize: 68}]
        ],
        challengeFriends : [
            ['SHARE WITH YOUR FRIENDS', {fontSize: 26}]
        ]
    }

    pages = null;

    constructor(game, options) {
        super(game);

        this.onSizeChange = this.onSizeChange.bind(this);
        this.onPlayAgainClick = _.debounce(this.onPlayAgainClick.bind(this), 10);

        _.extend(this, _.pick(options, 'stateSignals', 'isTopScore', 'score'));

        this.pages = {};
        this.name = 'GameOverScreen';

        this.init();
    }

    init() {
        this.alpha = 0;
        this.renderBackdrop();

        this.game.scale.onSizeChange.add(this.onSizeChange);
    }

    show() {
        return new Promise((resolve) => {
            this.showScore();
            const tween = this.game.add.tween(this).to({alpha: 1}, this.FADE_SPEED, Phaser.Easing.Linear.None, true);

            tween.onComplete.add(resolve);
        });
    }

    hide() {
        return new Promise((resolve) => {
            this.showPage();

            const tween = this.game.add.tween(this).to({alpha: 0}, this.FADE_SPEED, Phaser.Easing.Linear.None, true);

            tween.onComplete.add(resolve);
        });
    }

    renderBackdrop() {
        const {width, height} = this.game;

        this.backdrop = this.game.add.graphics();
        this.backdrop.beginFill(0, 0.7);
        this.backdrop.drawRect(0, 0, width, height);
        this.backdrop.endFill();
        this.backdrop.inputEnabled = true;

        this.add(this.backdrop);

        this.backdrop.alignIn(this.game.world.bounds, Phaser.CENTER);
    }

    onSizeChange() {
        const {width, height} = this.game.world.bounds;

        this.backdrop.width = width;
        this.backdrop.height = height;

        for (const page of Object.values(this.pages)) {
            this.scaleAndAlign(page);
        }
    }

    showScore() {
        if (this.showPage('score')) {
            return;
        }

        this.pages.score = this.renderScore();
        this.add(this.pages.score);
        this.showPage('score');
    }

    showLeaders(leaders) {
        if (this.showPage('leaders')) {
            return;
        }

        this.pages.leaders = this.renderLeaders(leaders);
        this.add(this.pages.leaders);
        this.showPage('leaders');
    }

    showPage(pageName) {
        if (!(pageName in this.pages) && _.isString(pageName)) {
            return false;
        }

        if (this.pageChangeIncomplete) {
            return true;
        }

        for (const [key, page] of Object.entries(this.pages)) {
            const toShow = key === pageName;

            if (page.visible && !toShow) {
                const tweenOut = this.game.add.tween(page)
                    .to({y: this.game.world.bounds.height + 100, alpha: 0}, this.PAGE_CHANGE_SPEED, this.PAGE_CHANGE_EASING.Out, true);

                tweenOut.onComplete.add((function(page) {
                    page.visible = false;
                    page.alpha = 1;
                }).bind(this, page));
            }

            if (!page.visible && toShow) {
                this.pageChangeIncomplete = true;
                page.alpha = 0;
                page.visible = true;
                this.scaleAndAlign(page);

                const targetY = page.y;

                page.y = -this.game.world.bounds.height - 100;


                const tweenIn = this.game.add.tween(page)
                    .to({y: targetY, alpha: 1}, this.PAGE_CHANGE_SPEED, this.PAGE_CHANGE_EASING.In, true);

                tweenIn.onComplete.add((function(page) {
                    this.scaleAndAlign(page);
                    this.pageChangeIncomplete = false;
                }).bind(this, page));
            }
        }

        return true;
    }

    renderScore() {
        const group = this.game.add.group(this);

        this.writeTextInGroup(group, 0, this.content.scoreBlock, {score: this.score});

        if (this.isTopScore) {
            let nameForm = this.renderNameForm();

            nameForm.y = this.getBottomYOfGroup(group);
            group.add(nameForm);
        } else {
//             this.writeTextInGroup(group, 0, this.content.challengeFriends);
//             let socialBtnGroup = this.renderSocialBtnGroup(380);
            //
//             socialBtnGroup.y = this.getBottomYOfGroup(group) + 300;
//             group.add(socialBtnGroup);

            const buttonStyles = {...this.buttonStyles};

            buttonStyles.regular = {...buttonStyles.regular, fontSize: 30};

            const btnY = this.getBottomYOfGroup(group) + 40;
            const playAgainBtn = this.game.add.button(0, btnY, 'skee', this.onPlayAgainClick, this, 'playAgain.png', 'playAgain.png', null, null, group);
            const onViewLeaderboardClick = _.debounce(() => this.stateSignals.onSignalViewLeaders.dispatch(), 10);
            const viewLeaderboardBtn = this.game.add.button(0, btnY, 'skee', onViewLeaderboardClick, this, 'viewLeaderboard.png', 'viewLeaderboard.png', null, null, group);

            playAgainBtn.x = (playAgainBtn.width + 30 + viewLeaderboardBtn.width) / -2;
            viewLeaderboardBtn.x = playAgainBtn.x + playAgainBtn.width + 30;
        }

        group.alignIn(this.game.world.bounds, Phaser.CENTER);
        group.visible = false;

        return group;
    }

    renderLeaders(leaders) {
        const lineHeight = 45;
        const group = this.game.add.group(this);

        this.writeTextInGroup(group, 0, this.content.leaderTitle);
        let y = this.getBottomYOfGroup(group) + 10;
        let currentUserLine = null;

        leaders.forEach(({user, score, name}, i) => {
            const styles = {...this.defaultTextStyles, font: 'skeefont', fontSize: 36};
            const line = this.game.add.group(group);

            const numberGraph = TextHelpers.createBitmapTextField(-80, 0, styles, (i + 1) + '.', line);
            const nameGraph = TextHelpers.createBitmapTextField(-15, 0, styles, name.substring(0,3).toUpperCase(), line);
            const scoreGraph = TextHelpers.createBitmapTextField(120, 0, styles, score, line);

            numberGraph.anchor.setTo(1, 1);
            nameGraph.anchor.setTo(0.5, 1);
            scoreGraph.anchor.setTo(1, 1);

            line.y = y;

            y += lineHeight;

            if (user) {
                currentUserLine = line;
            }
        });

        if (currentUserLine) {
            this.game.add.tween(currentUserLine).to({alpha: 0.2}, 200, Phaser.Easing.Linear.None, true, 0, 10, true);
        }

//        this.writeTextInGroup(group, 0, this.content.challengeFriends);
//         let socialBtnGroup = this.renderSocialBtnGroup(300);
//
//         socialBtnGroup.y = this.getBottomYOfGroup(group) + 300;
//         group.add(socialBtnGroup);

        const buttonStyles = {...this.buttonStyles};

        buttonStyles.regular = {...buttonStyles.regular, font: 'skeefont', fontSize: 28, padding: 10};

        const playAgainBtn = this.game.add.button(-300, 300, 'skee', this.onPlayAgainClick, this, 'playAgain-2lines.png', 'playAgain-2lines.png', null, null, group);

        const backToLotteryBtn = this.game.add.button(185, 300, 'skee', this.onBackToLotteryClick, this, 'backToLottery.png', 'backToLottery.png', null, null, group);

        group.alignIn(this.game.world.bounds, Phaser.CENTER);
        group.visible = false;

        return group;
    }

    renderNameForm() {
        const group = this.game.add.group(this);
        this.writeTextInGroup(group, -20, this.content.madeTop);
        const inputStyles = {
            regular: {
                font: 'skeefont',
                fill: '#FFFFFF',
                fontSize: 74,
                width: 200,
                height: 80,
                cursorHeight: 60,
                backgroundAlpha: 0
            }
        };

        const nameInput = new BitmapTextInput(this.game, group, 'nameInput', 'AAA', inputStyles, {
            uppercase: true,
            lettersOnly: true,
            maxLength: 3,
            onEnterPressed: this.onNameSubmit.bind(this),
            onInput: nameInput => {
                const value = nameInput.getValue();

                submitBtn.visible = value.length > 0;
            }
        });

        nameInput.getGraphics().x = nameInput.getWidth() / -2;
        nameInput.getGraphics().y = this.getBottomYOfGroup(group) - 25;
        nameInput.focus();

        const underline = this.game.add.graphics(0, 0);

        underline.name = _.uniqueId('rect_');
        underline.lineStyle(0, 0, 0);
        underline.beginFill(0xFFFFFF, 1);
        underline.drawRect(0, 0, 180, 3);
        underline.endFill();

        underline.x = -90;
        underline.y = this.getBottomYOfGroup(group);

        group.add(underline);

        this.writeTextInGroup(group, 10, this.content.namePrompt);

        const submitY = this.getBottomYOfGroup(group) + 10;
        const submitBtn = this.game.add.button(0, submitY, 'skee', this.onNameSubmit.bind(this, nameInput), this, 'enterButton.png', 'enterButton.png', null, null, group);

        submitBtn.anchor.set(0.5, 0);

        return group;
    }

    renderSocialBtnGroup(width) {
        const aspect = 3.95;
        const group = this.game.add.group(this);
        const background = this.game.add.image(0, 0, 'skee', 'socialBackground.png', group);

        background.width = width;
        background.height = width / aspect;

        const size = width / aspect / 1.5;
        const y = width / aspect / 2;
        const step = width / 5;
        let x = step;

        this.renderBtn(group, x, y, size,'facebookIcon.png', () => {
            FBHelper.share(config.linkForShare, this.sharingMessage());
        });

        x += step * 1.5;

        this.renderBtn(group, x, y, size, 'smsIcon.png', () => {
            const body = encodeURIComponent(this.sharingMessage()) + ' ' + config.linkForShare;
            const separator = isIOS() ? '&' : '?';

            window.open(`sms:${separator}body=${body}`, '_self');
        });

        x += step * 1.5;

        this.renderBtn(group, x, y, size, 'emailIcon.png', () => {
            const body = encodeURIComponent(this.sharingMessage()) + ' ' + config.linkForShare;

            window.open(`mailto:?body=${body}&subject=${config.shareSubject}`, '_self');
        });


        group.x = -width / 2;

        return group;
    }

    renderBtn(group, x, y, size, image, onClick) {
        const btn = this.game.add.button(x, y, 'skee', onClick, this, image, image, null, null, group);
        btn.width = size;
        btn.height = size;
        btn.anchor.set(0.5, 0.5);

        return btn;
    }

    writeTextInGroup(group, yOffset, textBlock, data = {}) {
        let y = this.getBottomYOfGroup(group) + yOffset;
        for (const line of textBlock) {
            const styles = {...this.defaultTextStyles, ...line[1]};

            y += styles.fontSize * styles.lineHeight;

            const field = TextHelpers.createBitmapTextField(0, y, styles, _.template(line[0])(data), group);
            let anchorX;

            switch (styles.align) {
                case 'center':
                    anchorX = 0.5;
                    break;
                case 'right':
                    anchorX = 1;
                    break;
                default:
                    anchorX = 0;
            }

            field.anchor.setTo(anchorX, 1);
        }
    }

    onPlayAgainClick() {
        this.stateSignals.onSignalGameReady.dispatch();
    }

    onBackToLotteryClick() {
        this.stateSignals.onSignalGameOverExit.dispatch();
    }

    onNameSubmit(textInput) {
        const value = textInput.getValue();

        if (value.length > 0) {
            this.stateSignals.onSignalNameEntered.dispatch(value);
        }
    }

    sharingMessage() {
        return this.isTopScore ?
            _.template(config.shareMessageWithScoreTpl)({score: this.score}) : config.shareMessageTpl;
    }

    getBottomYOfGroup(group) {
        let max = 0;

        group.forEach(item => {
            max = Math.max(max, item.y + item.height);
        });

        return max;
    }

    getMaxWidthOfGroup(group) {
        let left = 0;
        let right = 0;

        group.forEach(item => {
            const multiplier = item.anchor ? item.anchor.x : 0;
            const currentX = item.x - multiplier * item.width;

            left = Math.min(left, currentX);
            right = Math.max(right, currentX + item.width);
        });

        return right - left;
    }

    scaleAndAlign(group) {
        const bounds = this.game.world.bounds;
        const groupHeight = this.getBottomYOfGroup(group);
        const groupMaxWidth = this.getMaxWidthOfGroup(group);
        const maxScaleByHeight = bounds.height / (groupHeight + 60);
        const maxScaleByWidth = bounds.width / (groupMaxWidth + 60);
        const maxScaleByDevice = this.game.device.desktop ? 1 : Math.max(1, (bounds.height * 0.75) / groupHeight);

        group.scale.setTo(Math.min(maxScaleByHeight, maxScaleByWidth, maxScaleByDevice));
        group.alignIn(this.game.world.bounds, Phaser.CENTER);
    }

    destroy(...args) {
        this.game.scale.onSizeChange.remove(this.onSizeChange);

        super.destroy(...args);
    }
}

export default GameOverScreen;



// WEBPACK FOOTER //
// ./app/src/display/GameOverScreen.js